import {
  SIMAPPE_PASSWORD_MAX_LENGTH,
  SIMAPPE_PASSWORD_MIN_LENGTH,
} from './vault-security-settings.util';

/**
 * Política de contraseña efectiva que expone el backend Vault
 * (`GET /api/admin/users/password-policy`, homologación Simappe SecurityPolicy).
 */
export interface AdminUserPasswordPolicy {
  min_password_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_numbers: boolean;
  require_special_chars: boolean;
}

/**
 * Política por defecto que aplica el backend cuando Simappe no tiene una configurada
 * (`source: "default"`). Se usa como fallback si el endpoint aún no responde/falla, para
 * que el front no deje pasar claves que el backend rechazaría con 422.
 */
export const DEFAULT_ADMIN_USER_PASSWORD_POLICY: AdminUserPasswordPolicy = {
  min_password_length: SIMAPPE_PASSWORD_MIN_LENGTH,
  require_uppercase: true,
  require_lowercase: true,
  require_numbers: true,
  require_special_chars: true,
};

/** Caracteres especiales aceptados por Simappe al validar contra su SecurityPolicy. */
const ADMIN_PASSWORD_SPECIAL_CHAR_RE = /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;/'`~]/;

function readBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const token = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'si', 'sí'].includes(token)) return true;
    if (['false', '0', 'no'].includes(token)) return false;
  }
  return fallback;
}

function readPositiveInt(value: unknown, fallback: number): number {
  const num = typeof value === 'string' ? Number(value) : value;
  if (typeof num !== 'number' || !Number.isFinite(num) || num <= 0) return fallback;
  return Math.round(num);
}

/** Normaliza la respuesta del endpoint con defaults y piso Simappe (8). */
export function normalizeAdminUserPasswordPolicy(raw: unknown): AdminUserPasswordPolicy {
  const source =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const min = readPositiveInt(
    source['min_password_length'],
    DEFAULT_ADMIN_USER_PASSWORD_POLICY.min_password_length,
  );
  return {
    min_password_length: Math.max(SIMAPPE_PASSWORD_MIN_LENGTH, min),
    require_uppercase: readBoolean(
      source['require_uppercase'],
      DEFAULT_ADMIN_USER_PASSWORD_POLICY.require_uppercase,
    ),
    require_lowercase: readBoolean(
      source['require_lowercase'],
      DEFAULT_ADMIN_USER_PASSWORD_POLICY.require_lowercase,
    ),
    require_numbers: readBoolean(
      source['require_numbers'],
      DEFAULT_ADMIN_USER_PASSWORD_POLICY.require_numbers,
    ),
    require_special_chars: readBoolean(
      source['require_special_chars'],
      DEFAULT_ADMIN_USER_PASSWORD_POLICY.require_special_chars,
    ),
  };
}

/** Longitud mínima efectiva (nunca por debajo del piso Simappe). */
export function resolveAdminUserPasswordMinLength(policy: AdminUserPasswordPolicy): number {
  return Math.max(SIMAPPE_PASSWORD_MIN_LENGTH, policy.min_password_length || 0);
}

export type AdminUserPasswordRuleState = 'pending' | 'met' | 'unmet';

export interface AdminUserPasswordRuleView {
  key: string;
  label: string;
  state: AdminUserPasswordRuleState;
}

/** Reglas a renderizar dinámicamente según la política vigente. */
export function resolveAdminUserPasswordRuleViews(
  policy: AdminUserPasswordPolicy,
  password: string,
): AdminUserPasswordRuleView[] {
  const trimmed = (password ?? '').trim();
  const empty = trimmed.length === 0;
  const state = (ok: boolean): AdminUserPasswordRuleState => {
    if (empty) return 'pending';
    return ok ? 'met' : 'unmet';
  };

  const min = resolveAdminUserPasswordMinLength(policy);
  const rules: AdminUserPasswordRuleView[] = [
    { key: 'min', label: `Al menos ${min} caracteres`, state: state(trimmed.length >= min) },
  ];
  if (policy.require_uppercase) {
    rules.push({
      key: 'uppercase',
      label: 'Al menos una letra mayúscula',
      state: state(/[A-Z]/.test(trimmed)),
    });
  }
  if (policy.require_lowercase) {
    rules.push({
      key: 'lowercase',
      label: 'Al menos una letra minúscula',
      state: state(/[a-z]/.test(trimmed)),
    });
  }
  if (policy.require_numbers) {
    rules.push({
      key: 'numbers',
      label: 'Al menos un número',
      state: state(/\d/.test(trimmed)),
    });
  }
  if (policy.require_special_chars) {
    rules.push({
      key: 'special',
      label: 'Al menos un carácter especial',
      state: state(ADMIN_PASSWORD_SPECIAL_CHAR_RE.test(trimmed)),
    });
  }
  return rules;
}

/**
 * Valida la contraseña contra la política antes de enviar al backend.
 * Devuelve el primer mensaje de error, o `null` cuando la contraseña cumple.
 */
export function validateAdminUserPasswordAgainstPolicy(
  policy: AdminUserPasswordPolicy,
  password: string,
): string | null {
  const trimmed = (password ?? '').trim();
  if (!trimmed) {
    return 'La contraseña es obligatoria.';
  }
  const min = resolveAdminUserPasswordMinLength(policy);
  if (trimmed.length < min) {
    return `La contraseña debe tener al menos ${min} caracteres.`;
  }
  if (trimmed.length > SIMAPPE_PASSWORD_MAX_LENGTH) {
    return `La contraseña no puede superar ${SIMAPPE_PASSWORD_MAX_LENGTH} caracteres.`;
  }
  if (policy.require_uppercase && !/[A-Z]/.test(trimmed)) {
    return 'La contraseña debe incluir al menos una letra mayúscula.';
  }
  if (policy.require_lowercase && !/[a-z]/.test(trimmed)) {
    return 'La contraseña debe incluir al menos una letra minúscula.';
  }
  if (policy.require_numbers && !/\d/.test(trimmed)) {
    return 'La contraseña debe incluir al menos un número.';
  }
  if (policy.require_special_chars && !ADMIN_PASSWORD_SPECIAL_CHAR_RE.test(trimmed)) {
    return 'La contraseña debe incluir al menos un carácter especial.';
  }
  return null;
}

/** `true` si la contraseña actual no cumple la política (bloquea guardar). */
export function doesAdminUserPasswordPolicyBlockSave(
  policy: AdminUserPasswordPolicy,
  password: string,
): boolean {
  return validateAdminUserPasswordAgainstPolicy(policy, password) !== null;
}
